import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { streamCanonicalEvents } from "@/lib/sseClient";
import { decideMode, mergeEvent, nextMode } from "@/lib/decisionGraphLive";
import { TERMINAL_RUN_STATES } from "@/lib/decisionGraph";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_ON = new Set(["mas.agents_mobilized", "verification.completed", "approval.recorded", "solver.classical_selected",
  "run.completed", "run.failed", "run.cancelled"]);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function isAuthError(e) {
  const s = e?.response?.status ?? e?.status ?? (typeof e?.message === "string" && /_(401|403)$/.test(e.message) ? Number(e.message.slice(-3)) : null);
  return s === 401 || s === 403;
}

// ONE stream path for replay, live and snapshot: full reconstruction + the
// canonical SSE stream via sseClient (Last-Event-ID resume; 401/403 = auth lost).
// mode: idle | replay | live | completed. In live mode every event is committed to
// the frame list the moment it arrives; eof (sent by the server only once the Run
// is terminal) moves live → completed and refreshes the reconstruction.
export default function useRunStream(runId) {
  const [recon, setRecon] = useState(null);
  const [events, setEvents] = useState([]);
  const [mode, setMode] = useState("idle");
  const [status, setStatus] = useState("loading");        // loading|ready|empty|error
  const [connection, setConnection] = useState("idle");   // idle|reconnecting|auth_lost
  const [lastEventAt, setLastEventAt] = useState(null);
  const abortRef = useRef(null);
  const modeRef = useRef("idle");

  const setModeBoth = useCallback((m) => { modeRef.current = m; setMode(m); }, []);

  const refreshRecon = useCallback(async (alive) => {
    try {
      const { data } = await axios.get(`${API}/canonical/runs/${runId}`, { withCredentials: true });
      if (alive()) setRecon(data);
      return data;
    } catch (e) {
      if (alive() && isAuthError(e)) { setConnection("auth_lost"); setStatus("error"); }
      return null;
    }
  }, [runId]);

  useEffect(() => {
    if (!runId) return undefined;
    setStatus("loading"); setEvents([]); setRecon(null); setModeBoth("idle");
    setConnection("idle"); setLastEventAt(null);
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    let live = true;
    const alive = () => live;
    let collected = [];

    (async () => {
      let data = null;
      try {
        ({ data } = await axios.get(`${API}/canonical/runs/${runId}`, { withCredentials: true }));
        if (!live) return;
        setRecon(data);
      } catch (e) {
        if (!live) return;
        if (isAuthError(e)) setConnection("auth_lost");
        setStatus("error");
        return;
      }
      const m = decideMode(data?.run?.status);
      setModeBoth(m);
      if (m === "live") setStatus("ready");

      await streamCanonicalEvents(API, runId, {
        signal: ac.signal,
        onReconnect: () => { if (live) setConnection("reconnecting"); },
        onEvent: (ev) => {
          if (!live) return;
          collected = mergeEvent(collected, ev);
          if (modeRef.current === "live") {
            setEvents(collected);
            setLastEventAt(Date.now());
            setConnection("idle");
            if (REFRESH_ON.has(ev.event_type)) refreshRecon(alive);
          }
        },
        onEof: async () => {
          if (!live) return;
          setEvents(collected);
          setConnection("idle");
          setStatus(collected.length ? "ready" : "empty");
          if (modeRef.current === "live") {
            // eof means the Run is terminal server-side; re-read until the
            // reconstruction reflects that (the API may still be warming up).
            let fresh = null;
            for (let i = 0; i < 30 && live; i++) {
              fresh = await refreshRecon(alive);
              if (fresh && TERMINAL_RUN_STATES.includes(fresh.run?.status)) break;
              await sleep(1500);
            }
            if (live) setModeBoth(nextMode("live", "eof", fresh?.run?.status));
          }
        },
        onError: (err) => {
          if (!live) return;
          if (isAuthError(err)) setConnection("auth_lost");
          setStatus(collected.length ? "ready" : "error");
        },
      });
    })();
    return () => { live = false; ac.abort(); };
  }, [runId, refreshRecon, setModeBoth]);

  return { recon, events, mode, status, connection, lastEventAt };
}
